import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { whatsAppAPI, metaAssetsAPI } from '../services/api'
import { Button, Card, Alert, Spinner, Select } from '../components/ui'
import { Formik, Form } from 'formik'
import { FormikInput, FormikSelect, FormikTextarea } from '../components/ui'
import * as Yup from 'yup'
import templateSamples from '../data/templateSamples.json'
import templateOptions from '../data/templateOptions.json'

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Template name is required')
    .max(512, 'Template name must be at most 512 characters'),
  language: Yup.string()
    .required('Language is required')
    .max(10, 'Language code must be at most 10 characters'),
  category: Yup.string()
    .required('Category is required')
    .oneOf(['utility', 'marketing', 'authentication'], 'Category must be utility, marketing, or authentication'),
  parameter_format: Yup.string()
    .oneOf(['named', 'numbered'], 'Parameter format must be named or numbered')
    .nullable(),
  header_text: Yup.string().nullable(),
  header_format: Yup.string().nullable(),
  body_text: Yup.string()
    .required('Body text is required'),
  footer_text: Yup.string().nullable(),
})

const TemplateFormPage = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditMode = !!id

  const [assets, setAssets] = useState([])
  const [loadingAssets, setLoadingAssets] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [selectedAssetId, setSelectedAssetId] = useState('')
  const [formValues, setFormValues] = useState(null)

  useEffect(() => {
    loadAssets()
    if (isEditMode) {
      loadTemplate()
    }
  }, [id])

  const loadAssets = async () => {
    try {
      setLoadingAssets(true)
      const response = await metaAssetsAPI.getAssets({ asset_type: 'whatsapp_account' })
      if (response.data.success) {
        const assetList = response.data.data?.data || response.data.data || []
        setAssets(assetList)
        if (assetList.length > 0 && !selectedAssetId) {
          setSelectedAssetId(assetList[0].id.toString())
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load assets')
    } finally {
      setLoadingAssets(false)
    }
  }

  const loadTemplate = async () => {
    if (!id) return
    try {
      setLoading(true)
      const response = await whatsAppAPI.getTemplates(selectedAssetId)
      if (response.data.success) {
        const templates = Array.isArray(response.data.data) ? response.data.data : []
        const template = templates.find(t => t.id.toString() === id)
        if (template) {
          setSelectedAssetId(template.asset_id.toString())
          
          // Extract components from template
          let components = []
          if (template.components) {
            if (typeof template.components === 'string') {
              try {
                components = JSON.parse(template.components)
              } catch (e) {
                components = []
              }
            } else {
              components = Array.isArray(template.components) ? template.components : []
            }
          }
          
          // Extract header, body, footer from components (handle both uppercase and lowercase)
          const headerComponent = components.find(c => c.type === 'HEADER' || c.type === 'header' || c.type?.toUpperCase() === 'HEADER')
          const bodyComponent = components.find(c => c.type === 'BODY' || c.type === 'body' || c.type?.toUpperCase() === 'BODY')
          const footerComponent = components.find(c => c.type === 'FOOTER' || c.type === 'footer' || c.type?.toUpperCase() === 'FOOTER')
          
          setFormValues({
            name: template.name || template.template_name || '',
            language: template.language || 'en_US',
            category: template.category?.toLowerCase() || 'utility',
            parameter_format: template.parameter_format?.toLowerCase() || 'named',
            header_text: headerComponent?.text || '',
            header_format: headerComponent?.format || 'TEXT',
            body_text: bodyComponent?.text || '',
            footer_text: footerComponent?.text || '',
          })
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load template')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!selectedAssetId) {
      setError('Please select an asset first')
      return
    }

    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      // Build components array from separate fields
      const components = []
      
      // Add header if provided
      if (values.header_text && values.header_text.trim()) {
        components.push({
          type: 'header', // Backend expects lowercase
          format: values.header_format || 'TEXT',
          text: values.header_text.trim(),
        })
      }
      
      // Add body (required)
      if (values.body_text && values.body_text.trim()) {
        components.push({
          type: 'body', // Backend expects lowercase
          text: values.body_text.trim(),
        })
      }
      
      // Add footer if provided
      if (values.footer_text && values.footer_text.trim()) {
        components.push({
          type: 'footer', // Backend expects lowercase
          text: values.footer_text.trim(),
        })
      }

      const templateData = {
        name: values.name,
        language: values.language,
        category: values.category,
        parameter_format: values.parameter_format || 'named',
        components: components,
      }

      if (isEditMode) {
        // Update template - adjust API call as needed
        // For now, we'll use create since edit API might not exist
        setError('Edit functionality will be implemented when API is available')
        setSaving(false)
        return
      } else {
        const response = await whatsAppAPI.createTemplate(selectedAssetId, templateData)
        if (response.data.success) {
          setSuccess('Template created successfully')
          setTimeout(() => {
            navigate('/templates')
          }, 1500)
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} template`)
      setTimeout(() => setError(null), 5000)
    } finally {
      setSaving(false)
      setSubmitting(false)
    }
  }

  const renderPreview = (values) => {
    // Build components array from form fields
    const components = []
    
    // Add header if provided
    if (values.header_text && values.header_text.trim()) {
      components.push({
        type: 'HEADER',
        format: values.header_format || 'TEXT',
        text: values.header_text.trim(),
      })
    }
    
    // Add body (required)
    if (values.body_text && values.body_text.trim()) {
      components.push({
        type: 'BODY',
        text: values.body_text.trim(),
      })
    }
    
    // Add footer if provided
    if (values.footer_text && values.footer_text.trim()) {
      components.push({
        type: 'FOOTER',
        text: values.footer_text.trim(),
      })
    }

    const bodyComponent = components.find(c => c.type === 'BODY')
    const headerComponent = components.find(c => c.type === 'HEADER')
    const footerComponent = components.find(c => c.type === 'FOOTER')
    const buttonComponents = components.filter(c => c.type === 'BUTTON')

    // Format text to show placeholders
    const formatText = (text) => {
      if (!text) return ''
      // Replace {{1}}, {{2}} with highlighted placeholders for preview
      return text.replace(/\{\{(\d+)\}\}/g, '<span class="bg-yellow-100 px-1 rounded">{{$1}}</span>')
                 .replace(/\{\{([a-zA-Z0-9_]+)\}\}/g, '<span class="bg-yellow-100 px-1 rounded">{{$1}}</span>')
    }

    return (
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Mobile Frame */}
        <div className="bg-gray-800 px-4 py-2 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <div className="text-white text-xs font-medium">WhatsApp</div>
          <div className="w-6"></div>
        </div>

        {/* Chat Header */}
        <div className="bg-green-600 px-4 py-3 flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
            <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
          </div>
          <div className="flex-1">
            <div className="text-white font-medium text-sm">Business Name</div>
            <div className="text-green-100 text-xs">online</div>
          </div>
        </div>

        {/* Template Message */}
        <div className="bg-gray-100 p-4 min-h-[400px]">
          <div className="bg-white rounded-lg shadow-sm p-4 max-w-[85%]">
            {/* Header */}
            {headerComponent && (
              <div className="mb-3">
                {headerComponent.format === 'IMAGE' && (
                  <div className="w-full h-32 bg-gray-200 rounded-lg flex items-center justify-center mb-2">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
                {headerComponent.format === 'VIDEO' && (
                  <div className="w-full h-32 bg-gray-200 rounded-lg flex items-center justify-center mb-2">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
                {headerComponent.format === 'DOCUMENT' && (
                  <div className="w-full h-16 bg-gray-200 rounded-lg flex items-center justify-center mb-2 px-3">
                    <svg className="w-6 h-6 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-xs text-gray-600">Document</span>
                  </div>
                )}
                {headerComponent.text && (
                  <div 
                    className="font-semibold text-gray-900 mb-2 whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{ __html: formatText(headerComponent.text) }}
                  />
                )}
              </div>
            )}

            {/* Body */}
            {bodyComponent ? (
              <div 
                className="text-gray-800 mb-3 whitespace-pre-wrap"
                dangerouslySetInnerHTML={{ __html: formatText(bodyComponent.text) }}
              />
            ) : (
              <div className="text-gray-400 italic mb-3">Enter body text to see preview</div>
            )}

            {/* Footer */}
            {footerComponent && (
              <div 
                className="text-xs text-gray-500 mt-3 pt-3 border-t border-gray-200 whitespace-pre-wrap"
                dangerouslySetInnerHTML={{ __html: formatText(footerComponent.text) }}
              />
            )}

            {/* Buttons */}
            {buttonComponents.length > 0 && (
              <div className="mt-4 space-y-2">
                {buttonComponents.map((button, index) => (
                  <button
                    key={index}
                    className="w-full text-left px-4 py-2 bg-green-50 hover:bg-green-100 rounded-lg text-sm text-green-700 border border-green-200"
                  >
                    {button.text || `Button ${index + 1}`}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  const assetOptions = assets.map(asset => ({
    value: asset.id.toString(),
    label: `${asset.asset_name} (${asset.asset_type})`
  }))

  const categoryOptions = templateOptions.categories
  const parameterFormatOptions = templateOptions.parameterFormats
  const languageOptions = templateOptions.languages

  const initialValues = formValues || {
    name: '',
    language: 'en_US',
    category: 'utility',
    parameter_format: 'named',
    components: JSON.stringify(templateSamples.sampleComponents, null, 2),
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              {isEditMode ? 'Edit Template' : 'Create Template'}
            </h1>
            <p className="text-gray-600 mt-1">
              {isEditMode ? 'Update your WhatsApp message template' : 'Create a new WhatsApp message template'}
            </p>
          </div>
          <Button
            variant="secondary"
            onClick={() => navigate('/templates')}
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Cancel
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-4">
          <Alert variant="danger" onClose={() => setError(null)}>
            {error}
          </Alert>
        </div>
      )}

      {success && (
        <div className="mb-4">
          <Alert variant="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        </div>
      )}

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={!!formValues}
      >
        {({ values, isSubmitting }) => (
          <div className="grid grid-cols-12 gap-6">
            {/* Left Side - Form (8 columns) */}
            <div className="col-span-12 lg:col-span-8">
              <Card>
                <div className="p-6">
                  <Form className="space-y-4">
                    <Select
                      id="asset"
                      label="Select Asset *"
                      options={assetOptions}
                      placeholder={loadingAssets ? "Loading assets..." : "Select an asset"}
                      value={selectedAssetId}
                      onChange={(e) => setSelectedAssetId(e.target.value)}
                      disabled={loadingAssets || assets.length === 0 || isEditMode}
                      required
                    />

                    <FormikInput
                      id="name"
                      name="name"
                      label="Template Name"
                      placeholder="e.g., order_confirmation"
                      required
                      helpText="Template name must be unique and contain only lowercase letters, numbers, and underscores"
                    />

                    <FormikSelect
                      id="language"
                      name="language"
                      label="Language"
                      options={languageOptions}
                      required
                    />

                    <FormikSelect
                      id="category"
                      name="category"
                      label="Category"
                      options={categoryOptions}
                      required
                    />

                    <FormikSelect
                      id="parameter_format"
                      name="parameter_format"
                      label="Parameter Format"
                      options={parameterFormatOptions}
                      placeholder="Select parameter format"
                    />

                    <div className="space-y-4 border-t border-gray-200 pt-4">
                      <h3 className="text-lg font-semibold text-gray-800">Template Components</h3>
                      
                      {/* Header */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label htmlFor="header_format" className="block text-sm font-medium text-gray-700">
                            Header Format (Optional)
                          </label>
                        </div>
                        <FormikSelect
                          id="header_format"
                          name="header_format"
                          options={templateSamples.headerFormats}
                          placeholder="Select header format"
                        />
                        <FormikTextarea
                          id="header_text"
                          name="header_text"
                          rows={3}
                          placeholder="Enter header text (optional)"
                          className="mt-2"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Header text is optional. Use {'{{1}}'}, {'{{2}}'} for dynamic values.
                        </p>
                      </div>

                      {/* Body */}
                      <div>
                        <label htmlFor="body_text" className="block text-sm font-medium text-gray-700 mb-1">
                          Body <span className="text-red-500">*</span>
                        </label>
                        <FormikTextarea
                          id="body_text"
                          name="body_text"
                          rows={6}
                          placeholder="Enter message body text"
                          required
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Main message content. Use {'{{1}}'}, {'{{2}}'} for dynamic values.
                        </p>
                      </div>

                      {/* Footer */}
                      <div>
                        <label htmlFor="footer_text" className="block text-sm font-medium text-gray-700 mb-1">
                          Footer (Optional)
                        </label>
                        <FormikTextarea
                          id="footer_text"
                          name="footer_text"
                          rows={2}
                          placeholder="Enter footer text (optional)"
                        />
                        <p className="mt-1 text-xs text-gray-500">
                          Footer text is optional and appears at the bottom of the message.
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => navigate('/templates')}
                        disabled={isSubmitting || saving}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        loading={isSubmitting || saving}
                        disabled={isSubmitting || saving || !selectedAssetId}
                      >
                        {isEditMode ? 'Update Template' : 'Create Template'}
                      </Button>
                    </div>
                  </Form>
                </div>
              </Card>
            </div>

            {/* Right Side - Preview (4 columns) */}
            <div className="col-span-12 lg:col-span-4">
              <div className="sticky top-6">
                <Card>
                  <div className="p-6">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4">Mobile Preview</h2>
                    <div className="flex justify-center">
                      <div className="w-full max-w-[320px]">
                        {renderPreview(values)}
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        )}
      </Formik>
    </div>
  )
}

export default TemplateFormPage

